import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { featureFlags } from '@/utils/featureFlags';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { notAvailableYetHint } from '@/lib/notAvailableYet';
import { DrSkuDefaultNote } from './reports/DrSkuDefaultNote';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ReportsExportManager } from '@/components/organisation/ReportsExportManager';
import { ReportWarningsBanner } from '@/components/organisation/ReportWarningsBanner';
import SyncInProgressBanner from '@/components/organisation/SyncInProgressBanner';
import { useProgressiveReports } from '@/hooks/useProgressiveReports';
import { useVariantCatalog } from '@/hooks/useVariantCatalog';
import { variantMappingWorkbenchQueryKey } from '@/hooks/useVariantMappingWorkbench';
import { useTimezoneInfo } from '@/hooks/useTimezoneInfo';
import { Loader2, Calendar as CalendarIcon, Download, CheckCircle, Clock, Info } from 'lucide-react';
import { Calendar } from '@/components/ui/calendar';
import { format, subMonths } from 'date-fns';
import EditableSkuTable from '@/components/data-table/EditableSkuTable';
import SalesHistoryTable from '@/components/data-table/SalesHistoryTable';
import TimezoneBadge from '@/components/ui/timezone-badge';
import DataTableFooter from '@/components/data-table/DataTableFooter';
import MultiColumnFilter from '@/components/data-table/MultiColumnFilter';
import { FilterOperation } from '@/hooks/useConnectionData';
import { AmazonSchemaReportPill } from '@/components/organisation/settings/AmazonFinanceSourceSettings';
import { getReportsTabLabel } from '@/shared/reportsTabLabel';
import { buildReportSourceOptions, reportSourceKeyForTab, resolveReportSources } from '@/shared/reportDataSources';
import { parseReportDataSources } from '@/shared/types/reportDataSources';
import { connectionsWithAmazonAccountTypes, detectEcommercePlatform, getCsvEcommerceSubtype, isReportsSelectableConnection, isVendorOnlySelection, readStoredAmazonAccountType } from '@/shared/connectionPlatform';
import { fetchLinkedConnectors } from '@/utils/connectorApi';
import {
  fetchOrganisationDesignationDefaults,
  fetchVariantDesignationOverrides,
  useVariantMappingRows,
} from '@/hooks/useVariantMappingRows';
import { designationValuesByMaster } from '@/shared/variantMapping/designationValues';
import { DataSourceLine } from './reports/DataSourceLine';
import { defaultsFromRow } from '@/lib/designations/persistDesignationDefaults';
import { defaultsWithLeftmostColumn, type DesignationDefaults } from '@/lib/designations/organisationDefaults';
import {
  BOUGHT_AS_COLUMN,
  SOLD_AS_COLUMN,
  columnFiltersForReportQuery,
  filterRowsByDesignation,
  paginateRows,
  identifierValueToMasterId,
  projectSoldAsBoughtAsOntoReportRow,
  replaceProductColumnsWithDesignations,
  reportDaysInPeriod,
  reportRowIdentifierIsMapped,
  reportRowMasterProductId,
  totalsFromFilteredReportRows,
  type ReportDesignationOverride,
} from '@/lib/designations/reportEffectiveDesignation';

const EMPTY_DESIGNATION_DEFAULTS: DesignationDefaults = { soldAs: null, boughtAs: null };
const DESIGNATION_FILTER_FETCH_PAGE_SIZE = 100_000;

const isDesignationFilter = (column: string) =>
  column === SOLD_AS_COLUMN || column === BOUGHT_AS_COLUMN;

interface OrganisationReportsTabProps {
  organisation: any;
  /** Opens the merchant Settings tab, where the Expected Disposition Cost Default now lives (FIG-322). */
  onOpenSettings?: () => void;
}

const VARIANT_HISTORY_HINT = notAvailableYetHint('UC-223');

// Source lists, smart defaults and the per-merchant saved choice live in
// src/shared/reportDataSources.ts (FIG-345 UC-229).

const OrganisationReportsTab: React.FC<OrganisationReportsTabProps> = ({ organisation, onOpenSettings }) => {
  const queryClient = useQueryClient();
  const amazonIdsMissingAccountType = useMemo(() => {
    const connections = Array.isArray(organisation?.connections) ? organisation.connections : [];
    return connections
      .filter((connection: { type?: string | null; service?: string | null; connector_name?: string | null; amazon_account_type?: unknown; id?: string; connectionId?: string; connectorId?: string }) =>
        detectEcommercePlatform(connection) === 'amazon' && !readStoredAmazonAccountType(connection),
      )
      .map((connection: { id?: string; connectionId?: string; connectorId?: string }) => connection.connectorId || connection.id || connection.connectionId)
      .filter((id: string | undefined): id is string => typeof id === 'string' && id.length > 0);
  }, [organisation?.connections]);
  const { data: discoveredAmazonConnections = [] } = useQuery({
    queryKey: ['amazon-account-types', amazonIdsMissingAccountType],
    queryFn: () => fetchLinkedConnectors(amazonIdsMissingAccountType),
    enabled: amazonIdsMissingAccountType.length > 0,
    staleTime: 5 * 60 * 1000,
  });
  const connectionsForVendorCheck = useMemo(
    () => connectionsWithAmazonAccountTypes(
      Array.isArray(organisation?.connections) ? organisation.connections : [],
      discoveredAmazonConnections,
    ),
    [organisation?.connections, discoveredAmazonConnections],
  );
  const [activeReportTab, setActiveReportTab] = useState('skus');
  const [activeFilters, setActiveFilters] = useState<Array<{ column: string; value: string; operation: FilterOperation }>>([]);
  const [pendingAsOfDate, setPendingAsOfDate] = useState<Date | undefined>(undefined);
  const [tabResetSignal, setTabResetSignal] = useState(0);
  const prevNonPaginationKeyRef = useRef<string | null>(null);

  // Each reportable connection is a separate source (shared with Settings › Data sources).
  const getAvailableDataSources = useCallback(() => buildReportSourceOptions(organisation?.connections), [organisation]);
  // The merchant's saved source per report (FIG-345 UC-229), set in Settings.
  const savedReportSources = useMemo(() => parseReportDataSources(organisation?.report_data_sources), [organisation]);
  const sourcesForTab = useCallback(
    (tab: string) => resolveReportSources(savedReportSources[reportSourceKeyForTab(tab)], getAvailableDataSources()),
    [savedReportSources, getAvailableDataSources],
  );

  const getLoadingSourceLabel = useCallback((sourceKey: string) => {
    if (sourceKey === 'aggregated') return 'All Sources';

    const availableSource = getAvailableDataSources().find(source => source.id === sourceKey);
    return availableSource?.name || sourceKey;
  }, [getAvailableDataSources]);

  const getConnectionIdForSource = (): string => {
    try {
      if (!organisation?.connections || !Array.isArray(organisation.connections)) return '';

      const selectedSources = filters.dataSources || [];
      // If multiple sources selected, return empty string (will be handled by progressive hook)
      if (selectedSources.length !== 1) return '';

      // Find connection by ID for single source
      const found = organisation.connections.find((conn: any) =>
        (conn.id === selectedSources[0] || conn.connectionId === selectedSources[0])
      );

      return found ? (found.id || found.connectionId || '') : '';
    } catch {
      return '';
    }
  };

  const [filters, setFilters] = useState({
    trailing12Months: true, // Always use trailing 12 months with precomputed tables
    dataSources: [] as string[], // Array of connection IDs
    pageSize: 100,
    metricType: 'GMV', // For sales history filtering
    page: 1,
    asOfDate: '' as string // Custom end date for trailing 12 months (empty = today)
  });
  const vendorOnlySelection = isVendorOnlySelection(connectionsForVendorCheck, filters.dataSources || []);

  // Vendor Central has no Payout equivalent (FIG-452), so fall back to GMV for a vendor-only selection.
  useEffect(() => {
    if (vendorOnlySelection && filters.metricType === 'Payout') {
      setFilters((prev) => ({ ...prev, metricType: 'GMV' }));
    }
  }, [vendorOnlySelection, filters.metricType]);
  // Shown metric, so a stale Payout never renders for one frame before the effect above resets it.
  const displayedMetricType = vendorOnlySelection && filters.metricType === 'Payout' ? 'GMV' : filters.metricType;

  // Pagination state
  const [totalRows, setTotalRows] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalsData, setTotalsData] = useState<any>(null);

  // Memoized connection IDs for timezone info to avoid re-fetch loops
  const connectionIds = useMemo(() => {
    if (!organisation?.connections || !Array.isArray(organisation.connections)) return [];
    return organisation.connections
      .map((conn: any) => conn.id || conn.connectionId)
      .filter(Boolean);
  }, [organisation?.connections]);

  // Use timezone info hook
  const { timezoneInfo, loading: timezoneLoading, getTimezoneForConnection } = useTimezoneInfo(connectionIds);
  const [showExportManager, setShowExportManager] = useState(false);

  // Use progressive reports hook
  const {
    data: reportData,
    loading,
    generating,
    generationReason,
    loadingBySource,
    completedSources,
    error,
    fetchReportData,
    totalRows: hookTotalRows,
    totalRowsKnown,
    totalsData: hookTotalsData,
    totalsUnavailable,
    warnings,
    inventorySourceApplied,
  } = useProgressiveReports({
    organisation,
    onDataUpdate: (data, totalRows, totals) => {
      // Update pagination metadata
      if (totalRows !== undefined) setTotalRows(totalRows);
      if (totals) setTotalsData(totals);
    }
  });

  // UC-197: load Inc-17 designation defaults/overrides for Sold as / Bought as projection
  const organisationId = organisation?.id as string | undefined;
  const designationDefaultsQuery = useQuery({
    queryKey: ['organisation-designation-defaults', organisationId],
    enabled: Boolean(organisationId),
    queryFn: async () => defaultsFromRow(await fetchOrganisationDesignationDefaults(organisationId!)),
  });
  const designationOverridesQuery = useQuery({
    queryKey: ['variant-designation-overrides', organisationId],
    enabled: Boolean(organisationId),
    queryFn: () => fetchVariantDesignationOverrides(organisationId!),
  });
  const persistedDesignationDefaults = designationDefaultsQuery.data ?? EMPTY_DESIGNATION_DEFAULTS;
  const designationOverridesByMaster = useMemo(() => {
    const map = new Map<string, ReportDesignationOverride>();
    for (const row of designationOverridesQuery.data ?? []) {
      map.set(row.master_product_id, row);
    }
    return map;
  }, [designationOverridesQuery.data]);
  const variantCatalogQuery = useVariantCatalog(organisationId);
  const variantMappingQuery = useVariantMappingRows(organisationId);
  const designationMatrixReady =
    (variantCatalogQuery.isSuccess || variantCatalogQuery.isError) &&
    (variantMappingQuery.isSuccess || variantMappingQuery.isError);
  const valuesByMaster = useMemo(() => {
    if (!designationMatrixReady) return new Map<string, Record<string, string | null>>();
    return designationValuesByMaster(
      variantCatalogQuery.data ?? [],
      variantMappingQuery.data ?? [],
      { identifiersUnavailable: variantMappingQuery.isError || variantCatalogQuery.isError },
    );
  }, [
    designationMatrixReady,
    variantCatalogQuery.data,
    variantCatalogQuery.isError,
    variantMappingQuery.data,
    variantMappingQuery.isError,
  ]);
  const designationDefaults = useMemo(() => {
    if (!designationDefaultsQuery.isFetched || !designationMatrixReady) return persistedDesignationDefaults;
    const columnKeys: string[] = [];
    for (const values of valuesByMaster.values()) columnKeys.push(...Object.keys(values));
    return defaultsWithLeftmostColumn(persistedDesignationDefaults, columnKeys);
  }, [
    designationDefaultsQuery.isFetched,
    designationMatrixReady,
    persistedDesignationDefaults,
    valuesByMaster,
  ]);
  const identifierToMaster = useMemo(
    () => identifierValueToMasterId(valuesByMaster),
    [valuesByMaster],
  );

  // Update state when hook data changes
  React.useEffect(() => {
    if (hookTotalRows !== undefined) setTotalRows(hookTotalRows);
    if (hookTotalsData) setTotalsData(hookTotalsData);
  }, [hookTotalRows, hookTotalsData]);

  // Update default data sources when organisation changes
  // Smart default: prefer API connections over CSV when both exist for the same platform
  useEffect(() => {
    const defaultSourceIds = sourcesForTab(activeReportTab).ids;

    setFilters(prev => ({
      ...prev,
      trailing12Months: true,
      dataSources: defaultSourceIds,
      pageSize: 100,
      metricType: 'GMV',
      page: 1
    }));
  }, [organisation]);

  const resetAllFiltersForTabSwitch = (nextTab: string) => {
    const defaultSourceIds = sourcesForTab(nextTab).ids;

    setActiveFilters([]);
    setPendingAsOfDate(undefined);
    setFilters({
      trailing12Months: true,
      dataSources: defaultSourceIds,
      pageSize: 100,
      metricType: 'GMV',
      page: 1,
      asOfDate: ''
    });
    setCurrentPage(1);
    setTabResetSignal(prev => prev + 1);
  };

  const handleReportTabChange = (nextTab: string) => {
    if (nextTab === activeReportTab) return;
    resetAllFiltersForTabSwitch(nextTab);
    setActiveReportTab(nextTab);
  };

  // Convert activeFilters to columnFilters format for backend
  // Sold as / Bought as are not SQL columns. They are applied after the full candidate set is loaded.
  const getColumnFilters = () => {
    return columnFiltersForReportQuery(activeFilters).map(filter => ({
      column: filter.column,
      value: filter.value,
      operation: filter.operation
    }));
  };

  const retryReport = () => {
    if (organisationId) {
      void queryClient.invalidateQueries({ queryKey: variantMappingWorkbenchQueryKey(organisationId) });
    }
    fetchReportData(activeReportTab, { ...filters, columnFilters: getColumnFilters() });
  };

  useEffect(() => {
    // Guard: Don't fetch if data sources aren't ready yet
    if (!filters.dataSources || filters.dataSources.length === 0) return;

    const columnFilters = columnFiltersForReportQuery(activeFilters).map((filter) => ({
      column: filter.column,
      value: filter.value,
      operation: filter.operation,
    }));
    const designationFiltersActive = activeFilters.some((filter) => isDesignationFilter(filter.column));

    // Reset pagination when report type or non-pagination filters change.
    // Keep page/pageSize changes fetch-only (no forced reset).
    const nonPaginationKey = JSON.stringify({
      activeReportTab,
      dataSources: filters.dataSources,
      trailing12Months: filters.trailing12Months,
      metricType: filters.metricType,
      asOfDate: filters.asOfDate,
      columnFilters,
      designationFilters: activeFilters
        .filter((filter) => isDesignationFilter(filter.column))
        .map((filter) => `${filter.column}|${filter.operation}|${filter.value ?? ''}`),
    });

    const prevKey = prevNonPaginationKeyRef.current;
    const shouldResetPage = prevKey !== nonPaginationKey;
    prevNonPaginationKeyRef.current = nonPaginationKey;

    // Avoid double-fetch: if we need to reset to page 1 and we're not already there,
    // update state and let the effect re-run with the new page.
    if (shouldResetPage && (filters.page || 1) !== 1) {
      setFilters(prev => ({ ...prev, page: 1 }));
      setCurrentPage(1);
      return;
    }

    const effectivePage = shouldResetPage ? 1 : (filters.page || 1);

    const filtersWithColumnFilters = {
      ...filters,
      page: designationFiltersActive ? 1 : effectivePage,
      pageSize: designationFiltersActive ? DESIGNATION_FILTER_FETCH_PAGE_SIZE : filters.pageSize,
      skipPagination: designationFiltersActive,
      columnFilters,
    };

    if (shouldResetPage) {
      setCurrentPage(1);
    }

    fetchReportData(activeReportTab, filtersWithColumnFilters);
  }, [
    activeReportTab,
    fetchReportData,
    activeFilters,
    filters.dataSources,
    filters.trailing12Months,
    filters.metricType,
    filters.asOfDate,
    filters.page,
    filters.pageSize,
  ]);

  useEffect(() => {
    // "All time" is only valid for Sales History; reset to trailing mode on other report tabs.
    if (activeReportTab !== 'sales-history' && !filters.trailing12Months) {
      setFilters(prev => ({ ...prev, trailing12Months: true, page: 1 }));
      setCurrentPage(1);
    }
  }, [activeReportTab, filters.trailing12Months]);

  const activeSources = sourcesForTab(activeReportTab);

  const renderReportControls = () => (
    <div className="space-y-4 mb-6">
      {/* Header with Export Manager */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <h3 className="text-lg font-semibold text-gray-900">
            {/* UC-121 AC-10: "Products"/"Product" -> "Variants"/"Variant" at this
                variant/SKU-grain surface is UI copy only -- no calculation, filter,
                backend, table, or model rename. */}
            {getReportsTabLabel(activeReportTab)}
          </h3>
          {activeReportTab === 'skus' && inventorySourceApplied != null && (
            <Badge
              variant="outline"
              className="text-xs text-muted-foreground bg-muted/40 border-border"
            >
              {`Inventory source: ${inventorySourceApplied === 'amazon'
                  ? 'Amazon'
                  : inventorySourceApplied === 'amazon_csv'
                    ? 'Amazon CSV'
                    : inventorySourceApplied === 'shopify'
                      ? 'Shopify'
                      : inventorySourceApplied === 'shopify_csv'
                        ? 'Shopify CSV'
                        : inventorySourceApplied === 'veeqo'
                          ? 'Veeqo'
                          : 'Per-connection'
                }`}
            </Badge>
          )}
          <AmazonSchemaReportPill
            organisationId={organisation?.id}
            connections={organisation?.connections}
            financeSource={organisation?.amazon_finance_source}
          />

          {/* Display timezone information for selected data sources */}
          {(() => {
            const selectedSources = filters.dataSources || [];
            if (selectedSources.length > 0) {
              // Get timezones for selected sources
              const selectedTimezones = selectedSources
                .map(id => getTimezoneForConnection(id))
                .filter(Boolean)
                .reduce((unique, tz) => {
                  const key = `${tz!.detected_timezone}_${tz!.timezone_offset}`;
                  if (!unique.find(u => `${u.detected_timezone}_${u.timezone_offset}` === key)) {
                    unique.push(tz!);
                  }
                  return unique;
                }, [] as any[]);

              if (selectedTimezones.length > 0) {
                const hasMultipleTimezones = selectedTimezones.length > 1;
                return (
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-500">
                      {hasMultipleTimezones ? 'Mixed Timezones:' : 'Data Timezone:'}
                    </span>
                    <div className="flex items-center gap-1">
                      {selectedTimezones.map((tz, index) => (
                        <TimezoneBadge
                          key={index}
                          timezone={tz.detected_timezone}
                          offset={tz.timezone_offset}
                          size="sm"
                        />
                      ))}
                      {hasMultipleTimezones && (
                        <Badge variant="destructive" className="text-xs ml-2">
                          ⚠️ Mixed
                        </Badge>
                      )}
                    </div>
                  </div>
                );
              }
            }
            return null;
          })()}
        </div>

        {/* Export Manager Button */}
        <Button
          variant="outline"
          onClick={() => setShowExportManager(true)}
          className="text-sm"
        >
          <Download className="h-4 w-4 mr-2" />
          Export Manager
        </Button>
      </div>

      {/* Filter Controls - All on same row */}
      <div className="flex items-center gap-2 flex-wrap">
        {/* FIG-345 (UC-229): the source is saved per merchant in Settings › Reports and Charts. */}
        <DataSourceLine
          prefix={activeReportTab === 'fixed-costs' ? 'Data source (shared with ∑: Variants):' : 'Data source:'}
          label={activeSources.label}
          onOpenSettings={onOpenSettings}
        />

        {/* Date range mode for Sales History tab */}
        {activeReportTab === 'sales-history' && (
          <div className="flex items-center gap-2">
            <Select
              value={filters.trailing12Months ? 'trailing' : 'all'}
              onValueChange={(value) => {
                if (value === 'all') {
                  setFilters(prev => ({ ...prev, trailing12Months: false, asOfDate: '', page: 1 }));
                  setCurrentPage(1);
                } else {
                  setFilters(prev => ({ ...prev, trailing12Months: true, page: 1 }));
                  setCurrentPage(1);
                }
              }}
            >
              <SelectTrigger className="w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="trailing">Trailing 12 months</SelectItem>
                <SelectItem value="all">All time</SelectItem>
              </SelectContent>
            </Select>

            {/* As-of date picker only shown when trailing 12 months is active */}
            {filters.trailing12Months && (
              <Popover onOpenChange={(open) => {
                if (open) {
                  setPendingAsOfDate(filters.asOfDate ? new Date(filters.asOfDate + 'T12:00:00') : undefined);
                }
              }}>
                <PopoverTrigger asChild>
                  <Button variant={filters.asOfDate ? 'default' : 'outline'} className="text-sm">
                    <CalendarIcon className="h-4 w-4 mr-2" />
                    {filters.asOfDate
                      ? `As of ${format(new Date(filters.asOfDate + 'T12:00:00'), 'MMM d, yyyy')}`
                      : 'As of today'
                    }
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-4 bg-popover" align="start">
                  <div className="space-y-3">
                    <div className="text-sm font-semibold">Trailing 12 months ending on:</div>
                    <Calendar
                      mode="single"
                      selected={pendingAsOfDate}
                      onSelect={setPendingAsOfDate}
                      disabled={(date) => date > new Date()}
                      initialFocus
                      className="p-3 pointer-events-auto rounded-md border"
                    />
                    {pendingAsOfDate && (
                      <div className="text-xs text-muted-foreground text-center">
                        {format(subMonths(pendingAsOfDate, 12), 'MMM d, yyyy')} → {format(pendingAsOfDate, 'MMM d, yyyy')}
                      </div>
                    )}
                    <div className="flex items-center justify-between gap-2 pt-1">
                      {(filters.asOfDate || pendingAsOfDate) ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 text-xs"
                          onClick={() => {
                            setPendingAsOfDate(undefined);
                            setFilters(prev => ({ ...prev, asOfDate: '', page: 1 }));
                            setCurrentPage(1);
                          }}
                        >
                          Reset to today
                        </Button>
                      ) : <div />}
                      <Button
                        size="sm"
                        className="h-8"
                        disabled={!pendingAsOfDate}
                        onClick={() => {
                          if (pendingAsOfDate) {
                            const dateStr = format(pendingAsOfDate, 'yyyy-MM-dd');
                            setFilters(prev => ({ ...prev, asOfDate: dateStr, page: 1 }));
                            setCurrentPage(1);
                          }
                        }}
                      >
                        Apply
                      </Button>
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
            )}
          </div>
        )}

        {/* As-of date picker for SKUs tab */}
        {activeReportTab === 'skus' && (
          <Popover onOpenChange={(open) => {
            if (open) {
              setPendingAsOfDate(filters.asOfDate ? new Date(filters.asOfDate + 'T12:00:00') : undefined);
            }
          }}>
            <PopoverTrigger asChild>
              <Button variant={filters.asOfDate ? 'default' : 'outline'} className="text-sm">
                <CalendarIcon className="h-4 w-4 mr-2" />
                {filters.asOfDate
                  ? `Trailing 12 months as of ${format(new Date(filters.asOfDate + 'T12:00:00'), 'MMM d, yyyy')}`
                  : 'Trailing 12 months'
                }
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-4 bg-popover" align="start">
              <div className="space-y-3">
                <div className="text-sm font-semibold">Trailing 12 months ending on:</div>
                <Calendar
                  mode="single"
                  selected={pendingAsOfDate}
                  onSelect={setPendingAsOfDate}
                  disabled={(date) => date > new Date()}
                  initialFocus
                  className="p-3 pointer-events-auto rounded-md border"
                />
                {pendingAsOfDate && (
                  <div className="text-xs text-muted-foreground text-center">
                    {format(subMonths(pendingAsOfDate, 12), 'MMM d, yyyy')} → {format(pendingAsOfDate, 'MMM d, yyyy')}
                  </div>
                )}
                <div className="flex items-center justify-between gap-2 pt-1">
                  {(filters.asOfDate || pendingAsOfDate) ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 text-xs"
                      onClick={() => {
                        setPendingAsOfDate(undefined);
                        setFilters(prev => ({ ...prev, asOfDate: '', page: 1 }));
                        setCurrentPage(1);
                      }}
                    >
                      Reset to today
                    </Button>
                  ) : <div />}
                  <Button
                    size="sm"
                    className="h-8"
                    disabled={!pendingAsOfDate}
                    onClick={() => {
                      if (pendingAsOfDate) {
                        const dateStr = format(pendingAsOfDate, 'yyyy-MM-dd');
                        setFilters(prev => ({ ...prev, asOfDate: dateStr, page: 1 }));
                        setCurrentPage(1);
                      }
                    }}
                  >
                    Apply
                  </Button>
                </div>
              </div>
            </PopoverContent>
          </Popover>
        )}

        {/* Trailing 12 months badge for fixed-costs tab */}
        {activeReportTab === 'fixed-costs' && (
          <Button
            variant="default"
            onClick={() => { }}
            className="text-sm"
            disabled={true}
          >
            <CalendarIcon className="h-4 w-4 mr-2" />
            Trailing 12 months
          </Button>
        )}

        {/* Multi-column filter for SKUs tab */}
        {activeReportTab === 'skus' && (
          <MultiColumnFilter
            columns={columns}
            activeFilters={activeFilters}
            onAddFilter={handleAddFilter}
            onClearAllFilters={handleClearAllFilters}
            tableData={reportData || []}
          />
        )}

        {/* Metric Type Filter - only for Sales History */}
        {activeReportTab === 'sales-history' && (
          <Select value={displayedMetricType} onValueChange={(value) => {
            const newFilters = { ...filters, metricType: value };
            setFilters(newFilters);
          }}>
            <SelectTrigger className="w-36">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="GMV">∑ GMV</SelectItem>
              <SelectItem value="NMV">∑ NMV</SelectItem>
              <SelectItem value="Quantity">∑ Quantity</SelectItem>
              {!vendorOnlySelection && <SelectItem value="Payout">∑ Payout</SelectItem>}
            </SelectContent>
          </Select>
        )}
      </div>

      {/* Metric Description - only for Sales History */}
      {activeReportTab === 'sales-history' && (
        <div className="bg-muted/50 border border-border rounded-lg p-3.5">
          <div className="flex items-start gap-2.5">
            <Info className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <h4 className="font-semibold text-sm mb-1 text-foreground">
                {displayedMetricType === 'GMV' && '∑ GMV'}
                {displayedMetricType === 'NMV' && '∑ NMV'}
                {displayedMetricType === 'Quantity' && '∑ Quantity'}
                {displayedMetricType === 'Payout' && '∑ Payout'}
              </h4>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {vendorOnlySelection && displayedMetricType === 'GMV' && 'Gross Merchandise Value: Revenue of the units shipped to Amazon (Vendor Central shipped revenue). Customer returns are not deducted.'}
                {vendorOnlySelection && displayedMetricType === 'NMV' && 'Net Merchandise Value: Cost value of the units shipped to Amazon (Vendor Central shipped COGS). Customer returns are not deducted.'}
                {vendorOnlySelection && displayedMetricType === 'Quantity' && 'Total number of units shipped to Amazon during the period. Customer returns are not deducted.'}
                {!vendorOnlySelection && displayedMetricType === 'GMV' && 'Gross Merchandise Value: Total customer spend before discounts and fees. Calculated as: Quantity × Unit Price + Shipping & Gift Wrap.'}
                {!vendorOnlySelection && displayedMetricType === 'NMV' && 'Net Merchandise Value: Your true net product revenue. Calculated as: Quantity × Unit Price + Shipping & Gift Wrap, if included - Discounts - Refunds.'}
                {!vendorOnlySelection && displayedMetricType === 'Quantity' && 'Total number of units sold during the period.'}
                {displayedMetricType === 'Payout' && 'Net Revenue After Fees: The actual amount deposited to your account by the ecommerce system (Amazon/Shopify) after all fees, taxes, deductions, and refunds.'}
              </p>
            </div>
          </div>
        </div>
      )}

    </div>
  );


  // Extract columns from report data - must be before filter functions
  const columns = useMemo(() => {
    if (!reportData || reportData.length === 0) return [];
    const raw = Object.keys(reportData[0]).filter(key =>
      key !== 'sourceBreakdown' &&
      key !== 'data_source' &&
      key !== 'constituent_skus' &&
      key !== 'inventory_source_origin' &&
      key !== 'identifier_missing' &&
      key !== 'Marketplace' &&
      key !== 'marketplace' &&
      key !== 'total_count' &&
      key !== 'DR: SKU %' &&
      key !== '∑: NERV $' &&
      key !== 'master_product_id' &&
      key !== 'Master Product Id'
    );
    // UC-197: Filter Select column lists Sold as / Bought as instead of Product
    if (activeReportTab === 'skus') {
      return replaceProductColumnsWithDesignations(raw);
    }
    return raw;
  }, [reportData, activeReportTab]);

  // Filter handler functions
  const handleAddFilter = (column: string, value: string, operation: FilterOperation) => {
    setActiveFilters(prev => {
      const existingIndex = prev.findIndex(
        filter => filter.column === column && filter.operation === operation && filter.value === value
      );
      if (existingIndex >= 0) {
        return prev.filter((_, i) => i !== existingIndex);
      }
      return [...prev, { column, value, operation }];
    });
  };

  const handleRemoveFilter = (column: string, value: string, operation: FilterOperation) => {
    setActiveFilters(prev =>
      prev.filter(
        filter => !(filter.column === column && filter.value === value && filter.operation === operation)
      )
    );
  };

  const handleClearAllFilters = () => {
    setActiveFilters([]);
  };

  const handlePageSizeChange = (value: string) => {
    const newFilters = {
      ...filters,
      pageSize: parseInt(value),
      page: 1
    };
    setFilters(newFilters);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    const newFilters = { ...filters, page };
    setFilters(newFilters);
  };

  const renderPageSizeControl = () => (
    <div className="flex items-center justify-between mb-4">
      <div className="flex items-center gap-4 text-sm text-gray-600">
        <span>Page size:</span>
        <Select value={filters.pageSize.toString()} onValueChange={handlePageSizeChange}>
          <SelectTrigger className="w-20">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="100">100</SelectItem>
            <SelectItem value="250">250</SelectItem>
            <SelectItem value="500">500</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {activeReportTab === 'skus' && (
        // FIG-325 / REQ-040: the default moved to Settings › Valuation › Expected Sales Costs.
        <DrSkuDefaultNote
          drSkuPercentage={organisation?.dr_sku_percentage}
          isReseller={organisation?.merchant_type === 'reseller'}
          onOpenSettings={onOpenSettings}
        />
      )}
    </div>
  );

  let designationFilteredCount: number | null = null;
  let designationTotalPages: number | null = null;

  const renderDataTable = () => {
    // Show generating banner when backend is preparing data
    if (generating) {
      const isInventorySourceRegen = generationReason === 'inventory_source_changed';
      const isMappingRegen = generationReason === 'mapping_change';
      return (
        <div className="border border-border rounded-lg p-8 text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-primary" />
          <p className="font-medium text-foreground">
            {isInventorySourceRegen
              ? 'Regenerating report because the inventory source changed...'
              : isMappingRegen
                ? 'Variant mapping changed. Regenerating Products / Variants…'
                : 'Report data is being prepared...'}
          </p>
          <p className="text-sm text-muted-foreground mt-2">
            {isInventorySourceRegen
              ? 'This runs automatically and may take a few minutes. The page will update when ready.'
              : isMappingRegen
                ? 'Stale figures are not final. Retry if this banner stays up after a mapping confirm, edit, or unmatch.'
                : 'This may take a while. The page will update automatically when ready.'}
          </p>
          {isMappingRegen && (
            <Button
              variant="outline"
              size="sm"
              className="mt-4"
              onClick={retryReport}
            >
              Retry
            </Button>
          )}
        </div>
      );
    }

    // Show loading only if no data is available yet and we're still loading
    if (loading && (!reportData || reportData.length === 0)) {
      return (
        <div className="border border-border rounded-lg p-8 text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
          <p>Loading report data...</p>
        </div>
      );
    }

    // Prefer showing errors (including backend timeouts) over empty-state messaging
    if (error && (!reportData || reportData.length === 0)) {
      const message = error ?? 'Failed to load report data. Please retry.';

      return (
        <div className="border border-border rounded-lg p-8 text-center text-muted-foreground space-y-4">
          <div>
            <p className="font-medium text-foreground">We couldn’t load this report.</p>
            <p className="text-sm mt-2">{message}</p>
          </div>
          <div className="flex items-center justify-center gap-2">
            <Button
              variant="default"
              size="sm"
              onClick={retryReport}
            >
              Retry
            </Button>
          </div>
        </div>
      );
    }

    if (!reportData || reportData.length === 0) {
      return (
        <div className="border border-border rounded-lg p-8 text-center text-muted-foreground">
          <p>No data currently available for this report.</p>
          <p className="text-sm mt-2">Connect further data sources or wait for existing ones to finish uploading/syncing.</p>
        </div>
      );
    }

    // Data is already filtered and paginated by backend
    const visibleRows = reportData;

    // Check if we're still loading data from any source (for multiple sources mode)
    const isProgressiveLoading = filters.dataSources.length > 1 &&
      Object.values(loadingBySource).some(loading => loading);

    // Check if totals are still loading (for progressive loading mode)
    const areTotalsLoading = isProgressiveLoading;

    // Use EditableSkuTable for SKUs tab only
    if (activeReportTab === 'skus') {
      // Handle empty filtered results
      if (visibleRows.length === 0) {
        return (
          <div className="border border-gray-200 rounded-lg p-8 text-center text-gray-500">
            <p>No data matches the current filters</p>
            <p className="text-sm mt-2">Try adjusting or clearing your filters</p>
          </div>
        );
      }

      // Add DR: SKU % and ∑: NERV $ columns only for SKUs data
      // UC-197: project Sold as / Bought as from Inc-17 effective designations
      const projectedRows = visibleRows.map((row) => {
        const masterId = reportRowMasterProductId(row, identifierToMaster);
        const projected = projectSoldAsBoughtAsOntoReportRow(
          { ...row, master_product_id: masterId || row.master_product_id },
          designationDefaults,
          designationOverridesByMaster.get(masterId),
          valuesByMaster.get(masterId) ?? {},
          { applyUnmappedIdentifier: !reportRowIdentifierIsMapped(row, identifierToMaster) },
        );
        return {
          ...projected,
          'DR: SKU %': row['DR: SKU %'] || '0.00',
          '∑: NERV $': row['∑: NERV $'] || '0.00',
        };
      });
      const enrichedData = filterRowsByDesignation(projectedRows, activeFilters);
      const designationFiltersActive = activeFilters.some((filter) => isDesignationFilter(filter.column));
      const pageSize = filters.pageSize || 100;
      if (designationFiltersActive) {
        designationFilteredCount = enrichedData.length;
        designationTotalPages = Math.max(1, Math.ceil(enrichedData.length / pageSize));
      }
      const designationRows = designationFiltersActive
        ? paginateRows(enrichedData, filters.page || 1, pageSize)
        : enrichedData;
      const designationTotals = designationFiltersActive
        ? totalsFromFilteredReportRows(enrichedData, reportDaysInPeriod(filters))
        : totalsData;

      if (enrichedData.length === 0) {
        return (
          <div className="border border-gray-200 rounded-lg p-8 text-center text-gray-500">
            <p>No data matches the current filters</p>
            <p className="text-sm mt-2">Try adjusting or clearing your filters</p>
          </div>
        );
      }

      // Filter out Marketplace, Shipping Cost $, Connection Id columns
      // Always hide NEV projection columns, conditionally show NERV projections
      const alwaysHiddenColumns = [
        'NEV (In 1 Month)', 'NEV (In 3 Months)', 'NEV (In 6 Months)',
        'NEV (in 1 month)', 'NEV (in 3 months)', 'NEV (in 6 months)',
        'Velocity (units/day)', 'Days of Stock',
        'NERV 30d Proj $', 'NERV 60d Proj $', 'NERV 90d Proj $'
      ];
      const nervProjectionColumns = [
        'NERV (in 1 month)', 'NERV (in 3 months)', 'NERV (in 6 months)'
      ];
      const tableColumns = Object.keys(designationRows[0] ?? enrichedData[0]).filter(col => {
        // Always exclude these columns
        if (col === 'Marketplace' || col === 'Shipping Cost $' || col === 'Connection Id' || col === 'connection_id' || col === 'constituent_skus' || col === 'identifier_missing' || col === 'master_product_id' || col === 'Master Product Id' || col === 'inventory_source_origin' || col === 'Inventory Source Origin' || col === 'Product' || col === 'SKU') {
          return false;
        }
        // Always hide NEV projection columns
        if (alwaysHiddenColumns.includes(col)) {
          return false;
        }
        // Conditionally exclude NERV projection columns based on feature flag
        if (!featureFlags.showReportsNervProjections && nervProjectionColumns.includes(col)) {
          return false;
        }
        return true;
      });

      // Bought as then Sold as at the former Product/Variant position
      const orderedTableColumns = [
        BOUGHT_AS_COLUMN,
        SOLD_AS_COLUMN,
        ...tableColumns.filter((c) => c !== SOLD_AS_COLUMN && c !== BOUGHT_AS_COLUMN),
      ];

      // Only reorder NERV projection columns, keep all others in original order
      const nervColumnsOrder = ['∑: NERV $', 'NERV (in 6 months)', 'NERV (in 3 months)', 'NERV (in 1 month)'];

      // Find where the first NERV column appears in the original order
      const nervBaseIndex = orderedTableColumns.findIndex(col => nervColumnsOrder.includes(col));

      let sortedColumns = [...orderedTableColumns];

      if (nervBaseIndex !== -1) {
        // Remove all NERV columns from their current positions
        const nonNervColumns = orderedTableColumns.filter(col => !nervColumnsOrder.includes(col));
        const existingNervColumns = nervColumnsOrder.filter(col => orderedTableColumns.includes(col));

        // Find the insertion point based on where NERV columns should go
        const insertIndex = nonNervColumns.findIndex((col) => {
          const originalIdx = orderedTableColumns.indexOf(col);
          return originalIdx > nervBaseIndex;
        });

        if (insertIndex !== -1) {
          sortedColumns = [
            ...nonNervColumns.slice(0, insertIndex),
            ...existingNervColumns,
            ...nonNervColumns.slice(insertIndex)
          ];
        } else {
          sortedColumns = [...nonNervColumns, ...existingNervColumns];
        }
      }
      const connectionId = getConnectionIdForSource();

      return (
        <div className="space-y-4">
          <ReportWarningsBanner warnings={warnings} />
          <EditableSkuTable
            data={designationRows}
            columns={sortedColumns}
            organisationId={organisation?.id}
            connectionId={connectionId}
            isAdmin={true} // TODO: Get this from user context
            organisation={organisation}
            onDataRefresh={retryReport}
            isLoading={isProgressiveLoading}
            areTotalsLoading={areTotalsLoading}
            totalsData={designationTotals}
            totalsUnavailable={totalsUnavailable}
          />
        </div>
      );
    }

    // Use specialized SalesHistoryTable for sales-history tab
    if (activeReportTab === 'sales-history') {
      // Handle empty filtered results
      if (visibleRows.length === 0) {
        return (
          <div className="border border-gray-200 rounded-lg p-8 text-center text-gray-500">
            <p>No data matches the current filters</p>
            <p className="text-sm mt-2">Try adjusting or clearing your filters</p>
          </div>
        );
      }

      return (
        <div className="space-y-4">
          <SalesHistoryTable
            data={visibleRows}
            loading={loading && (!reportData || reportData.length === 0)}
            resetSignal={tabResetSignal}
            metricType={filters.metricType}
            timezoneInfo={(() => {
              if (filters.dataSources.length > 1) {
                // For multiple sources mode, return the first available timezone or null
                // SalesHistoryTable will need to be updated to handle multiple timezones
                const allTimezones = connectionIds
                  .map(id => getTimezoneForConnection(id))
                  .filter(Boolean);
                return allTimezones.length > 0 ? allTimezones[0] : null;
              } else {
                const connectionId = getConnectionIdForSource();
                return getTimezoneForConnection(connectionId);
              }
            })()}
          />

          {/* Progressive loading indicator for "All" mode in Sales History tab */}
          {isProgressiveLoading && (
            <div className="border border-gray-200 rounded-lg p-6 text-center bg-gray-50">
              <div className="flex items-center justify-center gap-4">
                <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
                <div className="flex flex-col items-start">
                  <p className="text-sm font-medium text-gray-700">Loading additional data sources...</p>
                  <div className="flex items-center gap-2 mt-1">
                    {Object.entries(loadingBySource).map(([source, isLoading]) => (
                      <Badge
                        key={source}
                        variant={isLoading ? "secondary" : "default"}
                        className="text-xs"
                      >
                        {isLoading ? (
                          <>
                            <Clock className="h-3 w-3 mr-1" />
                            {getLoadingSourceLabel(source)}
                          </>
                        ) : (
                          <>
                            <CheckCircle className="h-3 w-3 mr-1" />
                            {getLoadingSourceLabel(source)}
                          </>
                        )}
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      );
    }

    // For other tabs (fixed-costs), use regular table without DR: SKU % and ∑: NERV $ columns
    // Handle empty filtered results
    if (visibleRows.length === 0) {
      return (
        <div className="border border-gray-200 rounded-lg p-8 text-center text-gray-500">
          <p>No data matches the current filters</p>
          <p className="text-sm mt-2">Try adjusting or clearing your filters</p>
        </div>
      );
    }

    const tableColumns = Object.keys(visibleRows[0]).filter(col => col !== 'connection_id' && col !== 'Marketplace' && col !== 'marketplace');

    // Sort by data_source first, then by type
    const sortedRows = [...visibleRows].sort((a, b) => {
      const dataSourceA = a.data_source || a['Data Source'] || '';
      const dataSourceB = b.data_source || b['Data Source'] || '';
      const typeA = a.type || a['Type'] || '';
      const typeB = b.type || b['Type'] || '';

      // First sort by data source
      const sourceCompare = dataSourceA.localeCompare(dataSourceB);
      if (sourceCompare !== 0) return sourceCompare;

      // Then sort by type within the same data source
      return typeA.localeCompare(typeB);
    });

    return (
      <div className="space-y-4">
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <Table containerClassName="max-h-[600px] overflow-auto">
            <TableHeader>
              <TableRow>
                {tableColumns.map((column) => (
                  <TableHead key={column} className="sticky top-0 z-10 bg-white font-semibold border-b-2 border-gray-200 whitespace-nowrap">
                    {column.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedRows.map((row, index) => (
                <TableRow key={index}>
                  {tableColumns.map((column) => (
                    <TableCell key={column}>
                      {typeof row[column] === 'object' ? JSON.stringify(row[column]) : row[column]?.toString() || ''}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {/* Progressive loading indicator for "All" mode */}
        {isProgressiveLoading && (
          <div className="border border-gray-200 rounded-lg p-6 text-center bg-gray-50">
            <div className="flex items-center justify-center gap-4">
              <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
              <div className="flex flex-col items-start">
                <p className="text-sm font-medium text-gray-700">Loading additional data sources...</p>
                <div className="flex items-center gap-2 mt-1">
                  {Object.entries(loadingBySource).map(([source, isLoading]) => (
                    <Badge
                      key={source}
                      variant={isLoading ? "secondary" : "default"}
                      className="text-xs"
                    >
                      {isLoading ? (
                        <>
                          <Clock className="h-3 w-3 mr-1" />
                          {source}
                        </>
                      ) : (
                        <>
                          <CheckCircle className="h-3 w-3 mr-1" />
                          {source}
                        </>
                      )}
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <SyncInProgressBanner
        organisation={organisation}
        selectedConnectorIds={filters.dataSources.length > 0 ? filters.dataSources : undefined}
      />
      <Tabs value={activeReportTab} onValueChange={handleReportTabChange} className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="skus">{getReportsTabLabel('skus')}</TabsTrigger>
          <TabsTrigger value="fixed-costs">{getReportsTabLabel('fixed-costs')}</TabsTrigger>
          {/* FIG-325: Variant History needs a daily snapshot store (UC-223, FIG-239/FIG-312). */}
          <TabsTrigger value="variant-history" disabled title={VARIANT_HISTORY_HINT} aria-label={`Variant History. ${VARIANT_HISTORY_HINT}`}>
            Variant History
          </TabsTrigger>
          <TabsTrigger value="sales-history">{getReportsTabLabel('sales-history')}</TabsTrigger>
        </TabsList>

        <TabsContent value="skus" className="mt-6">
          <div className="space-y-4">
            {renderReportControls()}
            {renderPageSizeControl()}
            {renderDataTable()}
            {!generating && !(loading && (!reportData || reportData.length === 0)) && (
              <DataTableFooter
                totalCount={designationFilteredCount != null ? null : ((activeFilters.length === 0 && totalRowsKnown) ? totalRows : null)}
                filteredCount={designationFilteredCount != null ? designationFilteredCount : (activeFilters.length > 0 ? (reportData?.length || 0) : totalRows)}
                currentPage={currentPage}
                totalPages={designationTotalPages != null ? designationTotalPages : ((activeFilters.length === 0 && totalRowsKnown) ? Math.ceil(totalRows / filters.pageSize) : 1)}
                onPageChange={handlePageChange}
                pageSize={filters.pageSize}
              />
            )}
          </div>
        </TabsContent>

        <TabsContent value="fixed-costs" className="mt-6">
          {vendorOnlySelection ? (
            <p className="text-sm text-muted-foreground">Not available for Vendor Central</p>
          ) : (
          <div className="space-y-4">
            {renderReportControls()}
            {renderPageSizeControl()}
            {renderDataTable()}
            {!generating && !(loading && (!reportData || reportData.length === 0)) && (
              <DataTableFooter
                totalCount={(activeFilters.length === 0 && totalRowsKnown) ? totalRows : null}
                filteredCount={activeFilters.length > 0 ? (reportData?.length || 0) : totalRows}
                currentPage={currentPage}
                totalPages={(activeFilters.length === 0 && totalRowsKnown) ? Math.ceil(totalRows / filters.pageSize) : 1}
                onPageChange={handlePageChange}
                pageSize={filters.pageSize}
              />
            )}
          </div>
          )}
        </TabsContent>

        <TabsContent value="sales-history" className="mt-6">
          <div className="space-y-4">
            {renderReportControls()}
            {renderPageSizeControl()}
            {renderDataTable()}
            {!generating && !(loading && (!reportData || reportData.length === 0)) && (
              <DataTableFooter
                totalCount={(activeFilters.length === 0 && totalRowsKnown) ? totalRows : null}
                filteredCount={activeFilters.length > 0 ? (reportData?.length || 0) : totalRows}
                currentPage={currentPage}
                totalPages={(activeFilters.length === 0 && totalRowsKnown) ? Math.ceil(totalRows / filters.pageSize) : 1}
                onPageChange={handlePageChange}
                pageSize={filters.pageSize}
              />
            )}
          </div>
        </TabsContent>
      </Tabs>

      <ReportsExportManager
        organisation={organisation}
        isOpen={showExportManager}
        onClose={() => setShowExportManager(false)}
        activeReportTab={activeReportTab}
        filters={filters}
        activeFilters={activeFilters}
      />
    </div>
  );
};

export default OrganisationReportsTab;